import { browser } from '$app/environment';
import { writable, derived } from 'svelte/store';

import { fetchRpc } from '$lib/api/flow-rpc.api';
import { getCookie } from '$lib/helpers/session';
import { rpc } from '$lib/api/flow-rpc.types';

export interface ClientNotification {
  id: string;
  message: string;
}

const DISMISSED_COOKIE_NAME = 'dismissed_notifications';
const COOKIE_EXPIRY_DAYS = 30;

function getCookieDomain(): string {
  if (!browser) return '';
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length > 2) return parts.slice(1).join('.');
  return hostname;
}

function getDismissedIds(): string[] {
  if (!browser) return [];

  const cookies = document.cookie.split('; ');
  const cookie = cookies.find((str) => str.startsWith(`${DISMISSED_COOKIE_NAME}=`));

  if (!cookie) return [];

  const value = cookie.replace(`${DISMISSED_COOKIE_NAME}=`, '');
  return value.split(',').filter((id) => id.trim() !== '');
}

function setDismissedIds(ids: string[]): void {
  if (!browser) return;

  const domain = getCookieDomain();
  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_EXPIRY_DAYS);

  document.cookie = `${DISMISSED_COOKIE_NAME}=${ids.join(',')};domain=${domain};path=/;expires=${expires.toUTCString()};SameSite=Strict`;
}

// Notifications are the banners configured on the server (banner_index rpc).
// They are loaded by the root layout and pushed in via setNotifications.
export const notifications = writable<ClientNotification[]>([]);

export const hasActiveNotifications = derived(notifications, ($notifications) => $notifications.length > 0);

// Fetches the banners for the current session. The endpoint accepts either a
// user or a prospect token; with neither there is nothing to show. A failed
// fetch is not worth blocking the app for, so it resolves to an empty list.
export async function fetchClientNotifications(fetchFn: typeof fetch = fetch): Promise<ClientNotification[]> {
  if (!getCookie('token') && !getCookie('prospect_token')) return [];

  try {
    const { banners } = await fetchRpc({
      body: {},
      endpoint: rpc.bannerIndex,
      fetchFn,
      tokenNames: ['token', 'prospect_token'],
    });

    return banners.map(({ id, content }) => ({ id, message: content }));
  } catch {
    return [];
  }
}

// Replaces the current notifications, hiding any the user already dismissed.
export function setNotifications(list: ClientNotification[]): void {
  const dismissedIds = getDismissedIds();
  notifications.set(list.filter((n) => !dismissedIds.includes(n.id)));
}

export function dismissNotification(id: string): void {
  const dismissedIds = getDismissedIds();

  if (!dismissedIds.includes(id)) {
    dismissedIds.push(id);
    setDismissedIds(dismissedIds);
  }

  notifications.update((current) => current.filter((n) => n.id !== id));
}
